import classNames from 'classnames';
import { kebabCase } from 'lodash';
import { createRef, useRef } from 'react';

import { TEMPO } from 'context/BrandContext';
import { keys, directions } from 'helpers/tabsAccessibility';
import useBrand from 'hooks/useBrand';
import { toTitleCase } from 'utilities/strings';

const Tabs = ({ selectedElement = null, activeView, disabled, onChange, tabsProps }) => {
  const { tabs, containerAriaLabel, classes = {} } = tabsProps;

  const tabButtonRefs = useRef(tabs.map(() => createRef()));

  // Handle keydown on tabs
  function handleKeyDown(event) {
    const key = event.keyCode;
    switch (key) {
      case keys.end:
        event.preventDefault();
        tabButtonRefs.current[0].current.focus();
        break;
      case keys.home:
        event.preventDefault();
        tabButtonRefs.current[tabButtonRefs.current.length - 1].current.focus();
        break;
    }
  }

  // Handle keyup on tabs
  function handleKeyUp(event, index) {
    const key = event.keyCode;
    switch (key) {
      case keys.left:
      case keys.right:
        switchTabOnArrowPress(event, index);
        break;
      case keys.enter:
      case keys.space:
        activateTab(event.target, true);
        break;
    }
  }

  // When a tab is clicked, activateTab is fired to activate it
  function handleClick(event, label) {
    const tab = event.target;
    activateTab(tab, false);
    onChange(label);
  }

  // Activates any given tab panel
  function activateTab(tab, setFocus) {
    // Deactivate all other tabs
    deactivateTabs();

    // Remove tabindex attribute
    tab.removeAttribute('tabindex');

    // Set the tab as selected
    tab.setAttribute('aria-selected', 'true');

    // Set focus when required
    if (setFocus) {
      tab.focus();
    }
  }

  // Deactivate all tabs and tab panels
  function deactivateTabs() {
    tabButtonRefs.current.forEach(tabRef => {
      tabRef.current.setAttribute('tabindex', '-1');
      tabRef.current.setAttribute('aria-selected', 'false');
    });
  }

  // Either focus the next, previous, first, or last tab
  // depending on key pressed
  function switchTabOnArrowPress(event, tabPosition) {
    const pressed = event.keyCode;
    const direction = directions[pressed];

    if (direction) {
      if (tabButtonRefs.current[tabPosition + direction]) {
        tabButtonRefs.current[tabPosition + direction].current.focus();
      } else if (pressed === keys.left) {
        tabButtonRefs.current[0].current.focus();
      } else if (pressed === keys.right) {
        tabButtonRefs.current[tabButtonRefs.current.length - 1].current.focus();
      }
    }
  }

  const isSelected = label => activeView === label;
  const isTempo = useBrand(TEMPO);

  return (
    <div
      className={classNames('flex items-stretch', classes.container)}
      role="tablist"
      aria-label={containerAriaLabel}
    >
      {tabs.map((tab, index) => (
        <button
          ref={tabButtonRefs.current[index]}
          onKeyDown={handleKeyDown}
          onKeyUp={e => handleKeyUp(e, index)}
          onClick={e => handleClick(e, tab.label)}
          className={classNames(
            'btn rounded-none border-l-0 border-r-0 border-t-0 p-4 text-center transition-all',
            {
              [classes.active
                ? classes.active
                : isTempo
                  ? 'cursor-auto border-b-4 border-tempo-blue-90 bg-white-100 text-tempo-blue-90'
                  : 'cursor-auto border-b-4 border-green-100 bg-white-100 text-green-100']:
                isSelected(tab.label),
              [classes.inactive
                ? classes.inactive
                : isTempo
                  ? 'cursor-pointer border-b-0 bg-charcoal-50 text-charcoal-90'
                  : 'cursor-pointer border-b-0 bg-charcoal-50']: !isSelected(tab.label),
              hidden: tab.hidden,
            },
            classes.contentContainer,
            tab.className
          )}
          role="tab"
          key={index}
          aria-selected={isSelected(tab.label)}
          aria-controls={`${kebabCase(tab.label)}-tab`}
          tabIndex={isSelected(tab.label) ? '' : '-1'}
          id={`${kebabCase(tab.label)}`}
          disabled={disabled}
        >
          <span
            className={classNames({
              [classes.labelWrapper]: isSelected(tab.label),
            })}
          >
            {toTitleCase(tab.label)}
          </span>
          {isSelected(tab.label) && selectedElement}
        </button>
      ))}
    </div>
  );
};

Tabs.propTypes = {
  selectedElement: PropTypes.element,
  onChange: PropTypes.func.isRequired,
  activeView: PropTypes.string.isRequired,
  tabsProps: PropTypes.shape({
    containerAriaLabel: PropTypes.string,
    classes: PropTypes.object,
    tabs: PropTypes.array,
  }).isRequired,
  disabled: PropTypes.bool,
};

export default Tabs;
